import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// ============================================================
// API CONFIGURATION
// ============================================================

export const API_BASE_URL =
    import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

console.log('🌍 API Base URL:', API_BASE_URL);

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Mutex & Queue for handling concurrent token refreshes
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else if (token) {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/**
 * Safely refreshes the access token using the stored refresh token.
 * Uses a clean axios request without stale Authorization headers.
 */
export async function refreshAuthToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
        throw new Error('No refresh token available');
    }

    const selectedBranchId = localStorage.getItem('selectedBranchId') || undefined;

    // Use a clean axios instance to avoid interceptor recursion and stale auth headers
    const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken, selectedBranchId },
        { headers: { 'Content-Type': 'application/json' }, timeout: 15000 }
    );

    const accessToken = response.data?.data?.accessToken;
    if (!accessToken) {
        throw new Error('No access token returned in refresh response');
    }

    localStorage.setItem('accessToken', accessToken);

    if (response.data?.data?.selectedBranchId) {
        localStorage.setItem('selectedBranchId', response.data.data.selectedBranchId);
    }

    return accessToken;
}

// Track user activity for proactive token refresh and idle detection
let lastUserActivityTimestamp = Date.now();

export function recordUserActivity(): void {
    lastUserActivityTimestamp = Date.now();
}

export function getLastUserActivity(): number {
    return lastUserActivityTimestamp;
}

/**
 * Checks if the current JWT access token is nearing expiration (within thresholdSeconds).
 */
export function isAccessTokenExpiringSoon(thresholdSeconds: number = 240): boolean {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;

    try {
        const parts = token.split('.');
        if (parts.length !== 3) return false;
        const payload = JSON.parse(atob(parts[1]));
        if (!payload.exp) return false;

        const timeRemaining = payload.exp - Math.floor(Date.now() / 1000);
        return timeRemaining <= thresholdSeconds;
    } catch {
        return false;
    }
}

/**
 * Proactively refreshes the token if user has been active recently and token is expiring soon.
 */
export async function checkAndRefreshTokenIfActive(maxIdleMinutes: number = 15): Promise<boolean> {
    const idleTimeMinutes = (Date.now() - lastUserActivityTimestamp) / (60 * 1000);
    if (idleTimeMinutes > maxIdleMinutes) {
        // User is idle; let inactivity monitor handle warning/logout
        return false;
    }

    if (isAccessTokenExpiringSoon(300)) { // Expiring within 5 minutes
        if (!isRefreshing) {
            try {
                isRefreshing = true;
                const newToken = await refreshAuthToken();
                processQueue(null, newToken);
                return true;
            } catch (err) {
                console.warn('[apiClient] Proactive refresh failed:', err);
                return false;
            } finally {
                isRefreshing = false;
            }
        }
    }
    return false;
}

// ============================================================
// REQUEST INTERCEPTOR
// ============================================================

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // Record user activity on API requests
    recordUserActivity();

    // Attach access token
    const token = localStorage.getItem('accessToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    // Attach selected branch
    const branchId = localStorage.getItem('selectedBranchId');
    if (branchId) {
        config.headers['x-branch-id'] = branchId;
    }

    return config;
});

// ============================================================
// RESPONSE INTERCEPTOR
// ============================================================

apiClient.interceptors.response.use(
    (response) => response,

    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (!originalRequest) {
            return Promise.reject(error);
        }

        const url = originalRequest.url || '';
        const isAuthEndpoint =
            url.includes('/auth/login') ||
            url.includes('/auth/refresh') ||
            url.includes('/auth/logout');

        // Handle 401 Unauthorized for non-auth endpoints
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            const refreshToken = localStorage.getItem('refreshToken');

            if (!refreshToken) {
                // No refresh token: session is terminated
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('selectedBranchId');
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }

            if (isRefreshing) {
                // Another request is already refreshing; queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            resolve(apiClient(originalRequest));
                        },
                        reject: (err: any) => {
                            reject(err);
                        },
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const newAccessToken = await refreshAuthToken();
                processQueue(null, newAccessToken);
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return apiClient(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('selectedBranchId');
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);

export default apiClient;